/**
 * Conditional rule evaluation. Same function runs client-side (renderer
 * decides which fields to show / mark required as the user types) and
 * server-side (submit endpoint enforces required-if rules against the
 * stored definition).
 *
 * Drift between client and server here would let a bot bypass server-side
 * "required if X" checks. Single source of truth — both layers import
 * `evaluateConditions` from this file.
 */

import type { ConditionalRule, FormDefinition, FormValues } from './types';

export interface FieldVisibility {
  /** True if the field should be rendered. Hidden fields are NOT submitted. */
  visible: boolean;
  /**
   * Effective required-ness after conditional rules have been applied.
   * Derived from the field's static `required` plus any `require` /
   * `optional` actions whose `when` clauses currently match.
   */
  required: boolean;
}

export type VisibilityMap = Record<string, FieldVisibility>;

function valuesMatch(
  rule: ConditionalRule,
  fieldValue: string | number | boolean | string[] | null | undefined,
): boolean {
  switch (rule.op) {
    case 'is_empty':
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') return true;
      if (Array.isArray(fieldValue) && fieldValue.length === 0) return true;
      return false;
    case 'is_not_empty':
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') return false;
      if (Array.isArray(fieldValue) && fieldValue.length === 0) return false;
      return true;
    case 'equals':
      return fieldValue === rule.value;
    case 'not_equals':
      return fieldValue !== rule.value;
    case 'in': {
      const haystack = Array.isArray(rule.value) ? rule.value : [];
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((v) => haystack.includes(v));
      }
      return haystack.includes(String(fieldValue ?? ''));
    }
    case 'not_in': {
      const haystack = Array.isArray(rule.value) ? rule.value : [];
      if (Array.isArray(fieldValue)) {
        return !fieldValue.some((v) => haystack.includes(v));
      }
      return !haystack.includes(String(fieldValue ?? ''));
    }
    default:
      return false;
  }
}

/**
 * Compute the visibility / required state for every field in the
 * definition, given the current values. Rules cascade — a later rule
 * targeting the same field overrides earlier rules' actions.
 */
export function evaluateConditions(
  definition: FormDefinition,
  values: FormValues,
): VisibilityMap {
  const map: VisibilityMap = {};

  // Initialise from static `required` and assume visible. Hidden fields
  // have no `required` property and are always considered optional.
  for (const field of definition.fields) {
    const isRequired = field.type === 'hidden' ? false : Boolean(field.required);
    map[field.key] = { visible: true, required: isRequired };
  }

  // Apply rules in order — later rules override earlier ones.
  for (const rule of definition.conditionalRules) {
    const sourceVal = values[rule.sourceFieldKey];
    const matches = valuesMatch(rule, sourceVal);
    if (!matches) continue;

    const target = map[rule.targetFieldKey];
    if (!target) continue;

    switch (rule.action) {
      case 'show':
        target.visible = true;
        break;
      case 'hide':
        target.visible = false;
        break;
      case 'require':
        target.required = true;
        break;
      case 'optional':
        target.required = false;
        break;
    }
  }

  return map;
}

/** Convenience: list of field keys whose `visible` is true given current values. */
export function visibleFieldKeys(
  definition: FormDefinition,
  values: FormValues,
): string[] {
  const map = evaluateConditions(definition, values);
  return definition.fields
    .filter((f) => map[f.key]?.visible !== false)
    .map((f) => f.key);
}
